import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import { useProject } from '../../contexts/ProjectContext';
import { useFilter } from '../../contexts/FilterContext';
import {
  getFinOpsMetrics,
  getServiceBreakdown,
  getBurnRateTrend,
  getMonthlySpendTrend,
  getAIModelCosts,
  getBudgetAlerts,
  refreshCostData,
  getWorkloadCosts,
} from '../../services/cloudopsApi';
import { finopsAPI } from '../../services/api';
import { getCreditsSummary } from '../../services/fundingApi';
import { markCloudOpsStepComplete } from '../../hooks/useCloudOpsOnboardingStatus';
import BudgetMetrics, { BudgetMetricsData } from './components/BudgetMetrics';
import ServiceBreakdownChart from './components/ServiceBreakdownChart';
import BurnRateChart from './components/BurnRateChart';
import MonthlySpendChart from './components/MonthlySpendChart';
import AIModelCostsChart from './components/AIModelCostsTable';
import BudgetAlerts from './components/BudgetAlerts';
import FilterBar from './components/FilterBar';
import ProjectCostBreakdown from './components/ProjectCostBreakdown';
import WorkloadCostBreakdown from './components/WorkloadCostBreakdown';
import PartnerCentralCredits, { PartnerCentralCreditsData } from './components/PartnerCentralCredits';

interface FilterParams {
  clientId?: string;
  timePeriod: string;
  startDate?: string;
  endDate?: string;
}

interface FilterChangeValues {
  period?: string;
  customStartDate?: string;
  customEndDate?: string;
}

const FinOpsPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedClientId } = useClient();
  const { selectedProjectId } = useProject();
  const { filterType, selectedId: filterId } = useFilter();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // Filter state (from URL)
  const [timePeriod, setTimePeriod] = useState<string>(searchParams.get('period') || 'last_3_months');
  const [customStartDate, setCustomStartDate] = useState<string>(searchParams.get('startDate') || '');
  const [customEndDate, setCustomEndDate] = useState<string>(searchParams.get('endDate') || '');

  const [metrics, setMetrics] = useState<BudgetMetricsData | null>(null);
  const [serviceBreakdown, setServiceBreakdown] = useState<Record<string, unknown>[]>([]);
  const [burnRateTrend, setBurnRateTrend] = useState<Record<string, unknown>[]>([]);
  const [monthlySpend, setMonthlySpend] = useState<Record<string, unknown>[]>([]);
  const [aiModelCosts, setAIModelCosts] = useState<Record<string, unknown>[]>([]);
  const [alerts, setAlerts] = useState<Record<string, unknown>[]>([]);
  const [projectCosts, setProjectCosts] = useState<Record<string, unknown>[]>([]);
  const [workloadCosts, setWorkloadCosts] = useState<Record<string, unknown>[]>([]);
  const [partnerCentralCredits, setPartnerCentralCredits] = useState<PartnerCentralCreditsData | null>(null);

  // Mark onboarding step as complete when visiting this page
  useEffect(() => {
    markCloudOpsStepComplete('review_costs');
  }, []);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (timePeriod !== 'last_3_months') params.set('period', timePeriod);
    if (customStartDate) params.set('startDate', customStartDate);
    if (customEndDate) params.set('endDate', customEndDate);
    setSearchParams(params, { replace: true });
  }, [timePeriod, customStartDate, customEndDate, setSearchParams]);

  // Load all data when filters change
  useEffect(() => {
    loadAllData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, selectedProjectId, timePeriod, customStartDate, customEndDate, filterType, filterId]);

  const loadAllData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Global filter (project_id, workload_id, account_id, environment_id) is automatically
      // appended by cloudopsApiCall from localStorage — no need to pass it explicitly here.

      const filterParams: FilterParams = {
        clientId: selectedClientId || user?.client_id || undefined,
        timePeriod,
      };

      // Add custom date range if in custom mode
      if (timePeriod === 'custom' && customStartDate && customEndDate) {
        filterParams.startDate = customStartDate;
        filterParams.endDate = customEndDate;
      }

      // Load all data in parallel with individual error handling
      const results = await Promise.allSettled([
        getFinOpsMetrics(filterParams),
        getServiceBreakdown(filterParams),
        getBurnRateTrend(filterParams),
        getMonthlySpendTrend(filterParams),
        getAIModelCosts(filterParams),
        getBudgetAlerts(),
        finopsAPI.getProjectCosts(filterParams.clientId, filterParams.timePeriod),
        // getCreditsBreakdown(filterParams), // Disabled: Shows AWS promotional credits (confusing)
        Promise.resolve([]), // Placeholder for creditsBreakdown
        getCreditsSummary(filterParams.clientId), // Partner Central credits for the component
        getWorkloadCosts(filterParams),
      ]);

      // Extract successful results, use empty arrays/objects for failures
      const [
        metricsResult,
        breakdownResult,
        burnRateResult,
        spendResult,
        aiCostsResult,
        alertsResult,
        projectCostsResult,
        _creditsBreakdownResult,
        partnerCentralCreditsResult,
        workloadCostsResult,
      ] = results;

      if (metricsResult.status === 'fulfilled') {
        setMetrics(metricsResult.value);
      } else {
        console.error('Error loading metrics:', metricsResult.reason);
      }

      setServiceBreakdown(breakdownResult.status === 'fulfilled' ? breakdownResult.value : []);

      // Burn rate data now comes fully calculated from backend
      // Backend handles both AWS promotional credits and Partner Central credits
      const burnRateData = burnRateResult.status === 'fulfilled' ? burnRateResult.value : [];
      setBurnRateTrend(burnRateData);
      setMonthlySpend(spendResult.status === 'fulfilled' ? spendResult.value : []);
      setAIModelCosts(aiCostsResult.status === 'fulfilled' ? aiCostsResult.value : []);
      setAlerts(alertsResult.status === 'fulfilled' ? alertsResult.value : []);

      if (projectCostsResult.status === 'fulfilled' && projectCostsResult.value.success) {
        setProjectCosts(projectCostsResult.value.data);
      } else {
        setProjectCosts([]);
      }

      // Credits breakdown disabled - placeholder kept for destructuring alignment

      // Partner Central credits is optional
      if (partnerCentralCreditsResult.status === 'fulfilled') {
        setPartnerCentralCredits(partnerCentralCreditsResult.value);
      } else {
        setPartnerCentralCredits(null);
      }

      // Workload costs is optional
      if (workloadCostsResult.status === 'fulfilled') {
        setWorkloadCosts(Array.isArray(workloadCostsResult.value) ? workloadCostsResult.value : []);
      } else {
        setWorkloadCosts([]);
      }
    } catch (err: unknown) {
      console.error('Error loading FinOps data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load cost data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filters: FilterChangeValues) => {
    if (filters.period !== undefined) {
      setTimePeriod(filters.period);
    }
    if (filters.customStartDate !== undefined) {
      setCustomStartDate(filters.customStartDate);
    }
    if (filters.customEndDate !== undefined) {
      setCustomEndDate(filters.customEndDate);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);

    try {
      // Trigger async cost sync (returns immediately, sync runs in background)
      const filterParams = {
        clientId: selectedClientId || user?.client_id || undefined,
      };

      await refreshCostData(filterParams);

      // Reload current data immediately (shows existing data while sync runs)
      await loadAllData();

      // Show info that sync is running in background
      setError(null);
    } catch (err: unknown) {
      console.error('Error refreshing cost data:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh cost data');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              FinOps Dashboard
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Track costs and budget utilization for your projects
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {refreshing ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Refreshing...
              </>
            ) : (
              <>
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh Data
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <FilterBar
        timePeriod={timePeriod}
        customStartDate={customStartDate}
        customEndDate={customEndDate}
        onChange={handleFilterChange}
      />

      {/* Error State */}
      {error && (
        <div className="mb-6">
          {error.toLowerCase().includes('cost') ||
           error.toLowerCase().includes('finops') ||
           error.toLowerCase().includes('spend') ||
           error.toLowerCase().includes('monthly') ||
           error.toLowerCase().includes('fetch') ||
           error.toLowerCase().includes('budget') ? (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-medium text-blue-800 dark:text-blue-200">
                    AWS Environment Configuration Required
                  </h3>
                  <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                    <p>To view cost analytics and financial data, you need to configure your AWS environment connection.</p>
                    <p className="mt-2">This allows Aivilo Lens to securely access your AWS cost and usage data through cross-account role assumption.</p>
                  </div>
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={() => globalThis.location.href = '/hub/aws-environments'}
                      className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      Configure AWS Environments
                    </button>
                    <button
                      onClick={loadAllData}
                      className="bg-blue-100 dark:bg-blue-800 px-4 py-2 rounded-md text-sm font-medium text-blue-800 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-800 dark:text-red-200">{error}</p>
              <button
                onClick={loadAllData}
                className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      )}

      {/* Budget Alerts */}
      {alerts.length > 0 && (
        <div className="mb-6">
          <BudgetAlerts alerts={alerts as unknown as Parameters<typeof BudgetAlerts>[0]['alerts']} />
        </div>
      )}

      {/* Budget Metrics Cards */}
      {metrics && (
        <div className="mb-6">
          <BudgetMetrics metrics={metrics} />
        </div>
      )}

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Burn Rate Chart */}
        <BurnRateChart data={burnRateTrend as unknown as Parameters<typeof BurnRateChart>[0]['data']} loading={loading} />

        {/* Service Breakdown */}
        <ServiceBreakdownChart data={serviceBreakdown as unknown as Parameters<typeof ServiceBreakdownChart>[0]['data']} loading={loading} />
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* AI Model Costs */}
        <AIModelCostsChart data={aiModelCosts as unknown as Parameters<typeof AIModelCostsChart>[0]['data']} loading={loading} />

        {/* Monthly Spend Trend */}
        <MonthlySpendChart data={monthlySpend as unknown as Parameters<typeof MonthlySpendChart>[0]['data']} loading={loading} />
      </div>

      {/* Project Cost Breakdown */}
      <div className="mb-6">
        <ProjectCostBreakdown data={projectCosts as unknown as Parameters<typeof ProjectCostBreakdown>[0]['data']} loading={loading} />
      </div>

      {/* Workload Cost Breakdown */}
      <div className="mb-6">
        <WorkloadCostBreakdown data={workloadCosts as unknown as Parameters<typeof WorkloadCostBreakdown>[0]['data']} loading={loading} />
      </div>

      {/* Credits Breakdown Table - Disabled: Shows AWS promotional credits which are confusing */}
      {/* The Cost Trend chart shows Partner Central credits which is the primary credit tracking */}
      {/* TODO: Reimplement to show Partner Central credits breakdown by environment */}
      {/* <div className="mb-6">
          <CreditsBreakdownTable data={creditsBreakdown} loading={loading} />
        </div> */}

      {/* Partner Central Credits - Show if available */}
      <div className="mb-6">
        <PartnerCentralCredits data={partnerCentralCredits} burnRateTrend={burnRateTrend} loading={loading} />
      </div>

      {/* Export Button */}
      <div className="flex justify-end">
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
          Export Report
        </button>
      </div>
    </div>
  );
};

export default FinOpsPage;
